import { makeValuesPredicate, not } from '@sgme/fp';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ExposedFormStatus, FormStatus } from 'state/forms/models';
import { addElements } from 'state/utils/array';
import { isExternalQuoteId, isPricerId, isQuoteId, isProductId } from 'utils/string';

export function translateOrdersBasketFormStatus(status: FormStatus): ExposedFormStatus {
  switch (status) {
    case 'INITIALIZING':
      return 'Loading';
    case 'PATCHING':
      return 'Patching';
    case 'READY':
      return 'Ready';
    case 'REJECTED':
      return 'Rejected';
    case 'EXECUTED':
      return 'Success';
    case 'EXECUTED_WITH_WARNING':
      return 'Warning';
    case 'UNKNOWN_ERROR':
      return 'UnknownError';
    case 'FETCH_ERROR':
    case 'INITIALIZATION_ERROR':
      return 'ActionFailed';
    case 'DELETING':
      return 'Deleting';
    case 'QUOTE_REQUESTED':
    case 'QUOTING_AND_AUTHORIZING':
      return 'QuotingAndAuthorizing';
    case 'QUOTED_PENDING_AUTHORIZATION':
      return 'QuotedPendingAuthorization';
    case 'AUTHORIZED_PENDING_QUOTE':
      return 'AuthorizedPendingQuote';
    case 'BOOKING_REQUESTED':
    case 'BOOKING_ACKED':
    case 'EXECUTING':
      return 'Executing';
    case 'QUOTE_TIMEOUT':
      return 'QuoteTimeout';
    case 'TRADE_AUTHORIZATION_TIMEOUT':
      return 'TradeAuthorizationTimeout';
    case 'TRADE_TIMEOUT':
      return 'TradeTimeout';
    default:
      return 'NotReady';
  }
}

export const isOrdersBasketFormDisabledByStatus = makeValuesPredicate([
  'INITIALIZING',
  'INITIALIZATION_ERROR',
  'DELETING',
  'QUOTE_REQUESTED',
  'QUOTING_AND_AUTHORIZING',
  'QUOTED_PENDING_AUTHORIZATION',
  'AUTHORIZED_PENDING_QUOTE',
  'EXECUTING',
  'EXECUTED',
  'EXECUTED_WITH_WARNING',
  'QUOTE_TIMEOUT',
  'TRADE_AUTHORIZATION_TIMEOUT',
  'TRADE_TIMEOUT',
] satisfies FormStatus[]);

export const isOrdersBasketFormSelectableByStatus = not(
  makeValuesPredicate(['INITIALIZING', 'DELETING', 'EXECUTING'] satisfies FormStatus[]),
);

export function isOrdersBasketFormReadyToBook(form: StaticFormResult): boolean {
  const tradableQuoteAction = form.actions.find(action => action.name === 'TradableQuote');
  const bookWithExecutionAction = form.actions.find(action => action.name === 'BookWithExecution');
  const isTradableQuoteEnabled = tradableQuoteAction ? !(tradableQuoteAction.isDisabled ?? false) : false;
  const isBookWithExecutionEnabled = bookWithExecutionAction ? !(bookWithExecutionAction.isDisabled ?? false) : false;
  return isTradableQuoteEnabled || isBookWithExecutionEnabled;
}

export function getOrdersBasketFormReadyStatus(form: StaticFormResult): 'READY' | 'NOT_READY' {
  return isOrdersBasketFormReadyToBook(form) ? 'READY' : 'NOT_READY';
}

export const isOrdersBasketFormBookableByStatus = not(
  makeValuesPredicate([
    'DELETING',
    'DUPLICATING',
    'EXECUTING',
    'FETCH_ERROR',
    'INITIALIZATION_ERROR',
    'INITIALIZING',
    'NOT_READY',
    'NOT_RELOADED',
    'NOT_VALID',
    'PATCHING',
    'PATCH_ERROR',
    'QUOTED',
    'QUOTING',
    'QUOTE_REQUESTED',
    'QUOTING_AND_AUTHORIZING',
    'QUOTED_PENDING_AUTHORIZATION',
    'AUTHORIZED_PENDING_QUOTE',
    'EXECUTED',
    'EXECUTED_WITH_WARNING',
    'UNKNOWN_ERROR',
    'VALIDATING',
  ] satisfies FormStatus[]),
);

export const isOrdersBasketFormCancelableByStatus = makeValuesPredicate([
  'QUOTING_AND_AUTHORIZING',
  'QUOTED_PENDING_AUTHORIZATION',
  'AUTHORIZED_PENDING_QUOTE',
] satisfies FormStatus[]);

export const isOrdersBasketFormDeletableByStatus = not(
  makeValuesPredicate([
    'INITIALIZING',
    'DELETING',
    'QUOTE_REQUESTED',
    'QUOTING_AND_AUTHORIZING',
    'QUOTED_PENDING_AUTHORIZATION',
    'AUTHORIZED_PENDING_QUOTE',
    'EXECUTING',
    'BOOKING_REQUESTED',
  ] satisfies FormStatus[]),
);

export const isOrdersBasketFormTimeoutStatus = makeValuesPredicate([
  'QuoteTimeout',
  'TradeAuthorizationTimeout',
  'TradeTimeout',
] satisfies ExposedFormStatus[]);

export function verifyQuoteImportIdentifiers(
  identifiers: readonly string[],
): Record<'valid' | 'invalid', readonly string[]> {
  return identifiers.reduce(
    (acc, id) => {
      const isValidQuoteId = isQuoteId(id);
      const isValidExternalQuoteId = isExternalQuoteId(id);
      const isValidPricerQuoteId = isPricerId(id);
      const isValidProductId = isProductId(id);
      return isValidQuoteId || isValidExternalQuoteId || isValidPricerQuoteId || isValidProductId
        ? { ...acc, valid: addElements(acc.valid, id) }
        : { ...acc, invalid: addElements(acc.invalid, id) };
    },
    {
      valid: [] as readonly string[],
      invalid: [] as readonly string[],
    },
  );
}
